/**
 * LabelViewTest - 标签查看测试页面
 * 用于本地测试，无需路由和真实API
 * 
 * 访问方式：
 * 1. 在App.js中添加路由：<Route path="/test-view" element={<LabelViewTest />} />
 * 2. 访问：http://localhost:3001/test-view
 */

import React, { useState } from 'react';
import LabelView from './LabelView';
import LabelContentRenderer from '../components/label-content';
import './LabelView/styles/LabelView.css';

function LabelViewTest() {
  const [testMode, setTestMode] = useState('mock'); // 'mock' | 'api'
  const [labelId, setLabelId] = useState('');

  // 模拟标签数据（用于测试）
  const mockLabelData = {
    id: 'test123',
    labelName: '测试可口可乐',
    backgroundColor: '#ffffff',
    borderColor: '#e74c3c',
    backgroundImage: null,
    contentItems: [
      {
        contentType: 'text',
        contentData: {
          title: '<strong>欢迎访问标签</strong>',
          content: '<p>这是一个测试标签，用于验证上传功能和预览效果。</p>',
          titleColor: '#e74c3c',
          contentColor: '#333333',
          backgroundColor: '#f9f9f9'
        }
      },
      {
        contentType: 'image',
        contentData: {
          images: [
            {
              src: 'https://via.placeholder.com/400x300/1e8094/ffffff?text=测试图片1',
              width: 100
            },
            {
              src: 'https://via.placeholder.com/400x300/38bf9d/ffffff?text=测试图片2',
              width: 50
            }
          ],
          description: '这是图片描述文本',
          backgroundColor: '#ffffff'
        }
      },
      {
        contentType: 'product-basic',
        contentData: {
          productName: '测试可口可乐',
          ingredients: '水、白砂糖、二氧化碳、焦糖色、磷酸、咖啡因',
          netContent: '330ml',
          standardCode: 'GB/T 10792',
          manufacturer: '测试食品有限公司',
          address: '北京市朝阳区测试路123号',
          origin: '中国',
          productionDate: '2025-01-01',
          shelfLife: '12个月',
          storageConditions: '常温避光保存',
          productionLicense: 'SC12345678901234',
          serviceHotline: '029-81330339'
        }
      },
      {
        contentType: 'nutrition',
        contentData: {
          items: [
            { name: '能量', per100g: '180', unit: '千焦', nrv: '2%', locked: false },
            { name: '蛋白质', per100g: '0', unit: '克', nrv: '0%', locked: false },
            { name: '脂肪', per100g: '0', unit: '克', nrv: '0%', locked: false },
            { name: '碳水化合物', per100g: '10.6', unit: '克', nrv: '4%', locked: false },
            { name: '钠', per100g: '5', unit: '毫克', nrv: '0%', locked: false }
          ]
        }
      },
      {
        contentType: 'related-links',
        contentData: {
          links: [
            { enabled: true, icon: '🌐', name: '官方网站', url: 'https://example.com', order: 1 },
            { enabled: true, icon: '📱', name: '联系客服', url: 'tel:029-81330339', order: 2 },
            { enabled: true, icon: '📧', name: '邮箱反馈', url: 'mailto:support@example.com', order: 3 }
          ]
        }
      }
    ]
  };

  // 替换全局fetch，拦截API请求返回模拟数据
  React.useEffect(() => {
    if (testMode === 'mock') {
      // 保存原始fetch
      const originalFetch = window.fetch;
      
      // 替换fetch
      window.fetch = (url, options) => {
        // 拦截标签详情请求
        if (url.includes('/labels/') && !url.includes('/qrcode')) {
          console.log('🧪 测试模式：返回模拟数据');
          return Promise.resolve({
            ok: true,
            status: 200,
            json: () => Promise.resolve({
              success: true,
              data: mockLabelData
            })
          });
        }
        
        // 其他请求正常处理
        return originalFetch(url, options);
      };
      
      // 清理函数：恢复原始fetch
      return () => {
        window.fetch = originalFetch;
      };
    }
  }, [testMode]);

  return (
    <div style={{ 
      padding: '20px', 
      background: '#f0f0f0', 
      minHeight: '100vh' 
    }}>
      {/* 测试控制面板 */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        margin: '0 auto 20px'
      }}>
        <h2 style={{ marginTop: 0, color: '#2563EB' }}>
          📱 标签查看测试页面
        </h2>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px',
            fontWeight: '600',
            color: '#333'
          }}>
            测试模式：
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setTestMode('mock')}
              style={{
                padding: '10px 20px',
                border: testMode === 'mock' ? '2px solid #2563EB' : '2px solid #ddd',
                background: testMode === 'mock' ? '#eef2ff' : 'white',
                color: testMode === 'mock' ? '#2563EB' : '#666',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              🧪 模拟数据（推荐）
            </button>
            <button
              onClick={() => setTestMode('api')}
              style={{
                padding: '10px 20px',
                border: testMode === 'api' ? '2px solid #2563EB' : '2px solid #ddd',
                background: testMode === 'api' ? '#eef2ff' : 'white',
                color: testMode === 'api' ? '#2563EB' : '#666',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              🌐 真实API
            </button>
          </div>
        </div>

        {testMode === 'api' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              标签ID：
            </label>
            <input
              type="text"
              value={labelId}
              onChange={(e) => setLabelId(e.target.value)}
              placeholder="请输入标签ID（如：abc123）"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>
        )}

        <div style={{
          background: '#f9f9f9',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#666'
        }}>
          <strong style={{ color: '#2563EB' }}>说明：</strong><br/>
          • 模拟数据模式：使用预设的测试数据，无需后端<br/>
          • 真实API模式：从后端获取真实标签数据<br/>
          • 此页面用于测试预览效果，不影响生产环境
        </div>
      </div>

      {/* 标签查看组件 */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          color: '#333',
          textAlign: 'center'
        }}>
          ⬇️ 标签预览效果
        </h3>
        <div style={{
          border: '2px dashed #ddd',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          {testMode === 'mock' ? (
            // 直接渲染模拟数据
            <MockLabelView data={mockLabelData} />
          ) : (
            // 使用真实LabelView组件
            labelId ? (
              <LabelView labelId={labelId} />
            ) : (
              <div style={{ 
                padding: '60px 20px', 
                textAlign: 'center',
                color: '#999'
              }}>
                请输入标签ID
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// 使用统一渲染器的简化版LabelView（与实际显示保持一致）
function MockLabelView({ data }) {
  return (
    <div 
      style={{
        backgroundColor: data.backgroundColor,
        borderColor: data.borderColor,
        backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {data.labelName && (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          borderBottom: '1px solid #e8eaed'
        }}>
          <h1 style={{
            color: '#202124',
            fontSize: '24px',
            fontWeight: 700,
            margin: 0
          }}>
            {data.labelName}
          </h1>
        </div>
      )}

      <div style={{ padding: '16px' }}>
        {data.contentItems.map((item, index) => (
          <div key={index} style={{ marginBottom: '20px' }}>
            <LabelContentRenderer 
              item={{ type: item.contentType, data: item.contentData }} 
              mode="view"
            />
          </div>
        ))}
      </div>

      <div style={{
        background: '#f8f9fa',
        padding: '16px',
        textAlign: 'center',
        borderTop: '1px solid #e0e0e0'
      }}>
        <p style={{
          fontSize: '12px',
          color: '#999',
          margin: 0
        }}>
          访问时间：{new Date().toLocaleString('zh-CN')}
        </p>
      </div>
    </div>
  );
}

export default LabelViewTest;
